import { useQueryClient } from '@tanstack/vue-query';
import { officesQueries, officesKeys, staffQueries, staffKeys } from '@therame/api-client';
import { OfficeFullSizeDto, UserFullSizeDto } from '@therame/shared';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

type OfficeStore = {
  id: Ref<string | null>;
  name: Ref<string | null>;
  staff: Ref<UserFullSizeDto[]>;
  office: Ref<OfficeFullSizeDto>;
  clear: () => void;
  fetchOffice: (officeId: string) => Promise<void>;
  fetchStaffs: (q?: string) => Promise<Ref<UserFullSizeDto[]>>;
};

export const useOfficeStore = defineStore('office', (): OfficeStore => {
  const id = ref<string | null>(null);
  const name = ref<string | null>(null);
  const staff = ref<Array<UserFullSizeDto>>([]);
  const office = ref<OfficeFullSizeDto>(new OfficeFullSizeDto());

  const clear = () => {
    id.value = null;
    name.value = null;
    staff.value = [];
  };

  const queryClient = useQueryClient();

  const fetchOffice = async (officeId: string) => {
    office.value = await queryClient.fetchQuery({
      queryKey: officesKeys.byId(officeId),
      queryFn: () => officesQueries.getById(officeId),
    });
  };

  const fetchStaffs = async (q?: string) => {
    if (q) {
      staff.value = await queryClient.fetchQuery({
        queryKey: staffKeys.searchByOffice(id.value!, q),
        queryFn: () => staffQueries.searchByOffice(id.value!, q),
      });
    } else {
      staff.value = await queryClient.fetchQuery({
        queryKey: staffKeys.byOffice(id.value!),
        queryFn: () => staffQueries.getByOffice(id.value!),
      });
    }
    return staff;
  };

  const store: OfficeStore = { id, name, staff, office, clear, fetchOffice, fetchStaffs };
  return store;
});
